import type {
  LenderExecution,
  PricingMode,
  RateExplanation,
} from "../../../lib/agent-types";
import { centsToDollars } from "../../../lib/financials";
import { cn } from "../../../lib/utils";

interface RateExplorerViewProps {
  explanation: RateExplanation | null;
  executions: LenderExecution[];
  pricingMode: PricingMode;
  pricingModeNote?: string;
  loanContextLabel?: string;
  modeledRatePct?: number;
}

export function RateExplorerView({
  explanation,
  executions,
  pricingMode,
  pricingModeNote,
  loanContextLabel,
  modeledRatePct,
}: RateExplorerViewProps) {
  if (!explanation) {
    return (
      <div className="surface-panel p-4 text-sm text-muted-foreground">
        Rate explanation is not available for this scenario.
      </div>
    );
  }

  return (
    <section
      className="space-y-5"
      data-slot="rate-explorer"
      data-pricing-mode={pricingMode}
    >
      <Decomposition
        explanation={explanation}
        loanContextLabel={loanContextLabel}
        modeledRatePct={modeledRatePct ?? explanation.final_rate_pct}
      />
      {executions.length > 0 ? (
        <ExecutionsStrip executions={executions} />
      ) : pricingModeNote ? (
        <div
          className="rounded-2xl bg-muted/40 p-3 text-xs leading-5 text-foreground"
          data-slot="rate-explorer-pricing-mode-note"
        >
          {pricingModeNote}
        </div>
      ) : null}
      {/* The points-per-rate footnote is LLPA-specific. Suppress it on
          program_fee lanes (FHA/VA/USDA) — there are no points to
          translate, and the program-fee note above already explains
          how those products are priced. */}
      {pricingMode !== "program_fee" ? (
        <p
          className="text-[11px] leading-5 text-muted-foreground"
          data-slot="rate-explorer-footnote"
        >
          ⓘ Sill modeling assumption: ~0.25% rate per 1 point. CFPB notes
          points have no fixed rate value; real lender quotes vary by sheet,
          day, and product.
        </p>
      ) : null}
    </section>
  );
}

function Decomposition({
  explanation,
  loanContextLabel,
  modeledRatePct,
}: {
  explanation: RateExplanation;
  loanContextLabel?: string;
  modeledRatePct: number;
}) {
  return (
    <div className="space-y-3" data-slot="rate-explorer-decomposition">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Your rate, decomposed
        </h3>
        {loanContextLabel ? (
          <span className="text-xs text-muted-foreground">
            {loanContextLabel}
          </span>
        ) : null}
      </header>
      <table className="w-full text-sm" data-slot="rate-explorer-table">
        <tbody className="divide-y divide-border-soft">
          <tr>
            <td className="py-2 text-foreground">
              {capitalize(explanation.base_label)}
            </td>
            <td className="py-2 text-right font-medium tabular-nums text-foreground">
              {formatPct(explanation.base_rate_pct)}
            </td>
          </tr>
          {(explanation.adjustments ?? []).map((adj) => (
            <tr key={`${adj.reason}-${adj.adjustment_pct}`}>
              <td className="py-2 text-foreground">
                <span className="font-medium">{capitalize(adj.reason)}</span>
              </td>
              <td className="py-2 text-right font-medium tabular-nums text-foreground">
                {formatSignedPct(adj.adjustment_pct)}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-border">
            <td className="py-2 font-semibold text-foreground">
              Sill&apos;s modeled rate
            </td>
            <td className="py-2 text-right text-base font-semibold tabular-nums text-foreground">
              {formatPct(modeledRatePct)}
            </td>
          </tr>
        </tbody>
      </table>
      {(explanation.adjustments?.length ?? 0) > 0 &&
      modeledRatePct > explanation.base_rate_pct ? (
        <p className="text-xs leading-5 text-muted-foreground">
          Sill prices loan adjustments straight into the rate. Lenders may
          quote them as points or a credit instead — see the alternatives
          below.
        </p>
      ) : null}
    </div>
  );
}

function ExecutionsStrip({ executions }: { executions: LenderExecution[] }) {
  return (
    <div className="space-y-3" data-slot="rate-explorer-executions">
      <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        How a lender may quote the same adjustments
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {executions.map((exec) => (
          <ExecutionCard key={exec.kind} execution={exec} />
        ))}
      </div>
    </div>
  );
}

function ExecutionCard({ execution }: { execution: LenderExecution }) {
  const cash = execution.upfront_payment_cents;
  const cashLabel =
    cash === 0
      ? "$0 upfront"
      : `${cash > 0 ? "+" : "−"}$${Math.abs(centsToDollars(cash)).toLocaleString(
          "en-US",
          { maximumFractionDigits: 0 },
        )} upfront`;

  return (
    <article
      className={cn(
        "flex flex-col gap-2 rounded-2xl border border-border bg-card p-3",
        "text-sm",
      )}
      data-slot="rate-explorer-execution"
      data-execution-kind={execution.kind}
    >
      <header className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {execution.label}
      </header>
      <div className="text-xl font-semibold tabular-nums text-foreground">
        {formatPct(execution.note_rate_pct)}
      </div>
      <div className="text-xs font-medium tabular-nums text-muted-foreground">
        {cashLabel}
      </div>
      <p className="text-xs leading-5 text-muted-foreground">
        {execution.caption}
      </p>
    </article>
  );
}

function formatPct(value: number) {
  return `${value.toFixed(3)}%`;
}

function formatSignedPct(value: number) {
  const sign = value >= 0 ? "+" : "−";
  return `${sign}${Math.abs(value).toFixed(3)}%`;
}

function capitalize(s: string) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
